<template>
  <ContentContainer :loadingStatus="isLoading">
    <div class="room-type-list content-wrapper">
      <div class="page-header">{{ $t('roomType') }}</div>
      <div>
        <el-row>
          <el-col :span="4">
            <el-input
              :placeholder="$t('keyword')"
              v-model="keyword"
              @input="isSearching = true"></el-input>
          </el-col>
          <el-col :span="20">
            <router-link to="room-type/add" style="float: right">
              <el-button type="primary" icon="el-icon-plus">{{ $t('newRoomType') }}</el-button>
            </router-link>
          </el-col>
        </el-row>
        <div style="margin-top: 25px">
          <el-table
            id="room-type-list-table"
            :data="roomTypeListDataSource"
            :row-class-name="getRowClassName"
            :default-sort = "{prop: 'code', order: 'ascending'}"
            v-loading="isSearching"
            style="width: 100%"
            header-row-class-name="table-header-color">
            <el-table-column
              prop="code"
              :label="$t('code')">
            </el-table-column>
            <el-table-column
              :label="$t('name')">
              <template slot-scope="scope">{{ scope.row.name }}</template>
            </el-table-column>
            <el-table-column
              :label="$t('status')">
              <template slot-scope="scope">
                <el-tag
                  :type="scope.row.isEnable === 1 ? 'success' : 'danger'" size="mini"
                  disable-transitions>{{ getRoomTypeStatus(scope.row.isEnable) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('updatedDate')">
              <template slot-scope="scope">{{ dateFormat.long(new Date(scope.row.updatedAt)) }}</template>
            </el-table-column>
            <el-table-column align="center">
              <template slot-scope="scope">
                <el-button type="primary" size="small" icon="el-icon-edit" plain circle @click="onEditButtonClick(scope.row)"></el-button>
                <el-button type="danger" size="small" icon="el-icon-delete" plain circle @click="onRemoveRoomTypeButtonClick(scope.row)"></el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>
  </ContentContainer>
</template>

<script>
import i18nMessages from '@/translations/Hotel/ManageHotel/RoomType/List.i18n'
import AlertViewMixin from '@/mixins/alert-view-mixin'
import LoadingViewMixin from '@/mixins/loading-view-mixin'
import NotificationMixin from '@/mixins/notification-mixin'
import HotelRoomTypeApi from '@/api/hotel/room-type-api'
import ContentContainer from '@/components/ContentContainerComponent.vue'
import LocalStorageFactory from '@/factories/local-storage-factory'
import { getHotelIndexRoute } from '@/services/hotel-service'
import { filterDataByKeyword } from '@/services/general-service'
import { getStatusName } from '@/helpers'
import { mapState } from 'vuex'
import { debounce } from 'lodash'
import { dateFormat } from '@/helpers/datetime'

export default {
  name: 'HotelRoomTypeList',
  mixins: [AlertViewMixin, LoadingViewMixin, NotificationMixin],
  components: { ContentContainer },
  i18n: { messages: i18nMessages },
  created: async function () {
    if (this.activeHotelId === undefined || this.activeHotelId === '') {
      const companyDetail = LocalStorageFactory.getCompanyDetail()
      const routeName = await getHotelIndexRoute(companyDetail, this.$store)

      return this.$router.push({
        name: routeName
      })
    }

    try {
      this.isLoading = true
      this.roomTypeList = await HotelRoomTypeApi.list(this.activeHotelId)
    } catch (err) {
      this.alertError(err.message)
    }
    this.getRoomTypeListDataSource()
    this.isLoading = false
  },
  computed: {
    ...mapState({
      activeHotelId: state => state.general.activeHotelId
    })
  },
  watch: {
    keyword: debounce(function () {
      this.isSearching = false
    }, 300),
    isSearching: function (value) {
      if (!value) {
        this.getRoomTypeListDataSource()
      }
    }
  },
  data () {
    return {
      dateFormat,
      isLoading: true,
      keyword: '',
      roomTypeList: [],
      isSearching: false,
      roomTypeListDataSource: []
    }
  },
  methods: {
    getRoomTypeListDataSource: function () {
      const searchKeys = ['code', 'name']
      const filteredRoomTypeList = filterDataByKeyword(this.roomTypeList, this.keyword, searchKeys)
      this.roomTypeListDataSource = [...filteredRoomTypeList]
    },
    getRoomTypeStatus: function (status) {
      return this.$t(getStatusName(status))
    },
    onEditButtonClick: function (roomType) {
      this.$router.push({
        name: 'HotelRoomTypeEdit',
        params: {
          roomType: roomType
        }
      })
    },
    onRemoveRoomTypeButtonClick: async function (row) {
      const confirmMessage = this.$t('confirmRemoveRoomTypeMessage', {
        roomTypeName: row.name,
        roomTypeCode: row.code
      })
      const confirmButtonText = this.$t('confirmButtonText')
      const cancelButtonText = this.$t('cancelButtonText')
      const isConfirmRemove = await this.alertConfirmDelete(confirmMessage, confirmButtonText, cancelButtonText)

      if (!isConfirmRemove) {
        return false
      }

      this.removeRoomType(row.id)
    },
    removeRoomType: async function (roomTypeId) {
      try {
        this.startLoading()

        await HotelRoomTypeApi.remove(this.activeHotelId, roomTypeId)
        this.roomTypeList = this.roomTypeList.filter(item => item.id !== roomTypeId)
        this.getRoomTypeListDataSource()
        const notificationTitle = this.$t('removeRoomTypeSuccess')
        this.showNotificationSuccess(notificationTitle)
      } catch (err) {
        this.alertError(err.message)
      }

      this.stopLoading()
    },
    getRowClassName: function ({ row, rowIndex }) {
      if (row.isEnable === 1) {
        return ''
      }
      return 'disable-row'
    }
  }
}
</script>

<style scoped>
  #room-type-list-table {
    width: 100%;
    border: 1px solid #dddddd;
    border-radius: 5px;
    box-shadow: 0px 3px 5px 1px #eeeeee;
  }
  .el-table th {
    background-color: #eeeeee;
  }
  .el-form > div {
    text-align: left;
  }
</style>
